declare global {
  interface Window {
    __bootLog?: (msg: string) => void;
  }
}
const log = (msg: string) => window.__bootLog?.(msg);

log("[main] module imported");

import Phaser from "phaser";
log("[main] phaser imported v" + Phaser.VERSION);

import { config } from "./config";
log("[main] config ok, mode=" + config.mode);

import { initTelegram } from "./telegram";
import { BootScene } from "./scenes/BootScene/BootScene";
import { WorldScene } from "./scenes/WorldScene/WorldScene";
import { ApiError, getStoredToken } from "./api/client";
import { authenticate } from "./api/auth";
import { loadMe, type PlayerFull } from "./api/me";

async function bootstrap(): Promise<void> {
  const telegram = initTelegram();
  log(
    "[main] telegram init ok, user=" +
      (telegram.user ? telegram.user.firstName : "(null)") +
      " platform=" +
      telegram.platform,
  );

  let playerFull: PlayerFull | null = null;

  // Probuj zaladowac istniejaca sesje
  if (getStoredToken()) {
    log("[main] mam zapisany token, sprawdzam sesje przez GET /api/me");
    try {
      playerFull = await loadMe();
      log("[main] sesja OK, gracz zaladowany: " + playerFull.player.displayName);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        log("[main] sesja wygasla, wykonuje re-authenticate");
      } else {
        log("[main] loadMe blad: " + (err instanceof Error ? err.message : String(err)));
      }
    }
  } else {
    log("[main] brak zapisanego tokenu, pierwsza wizyta lub clear cache");
  }

  // Authenticate jesli brak sesji
  if (!playerFull) {
    if (!telegram.initData) {
      throw new Error(
        "Brak Telegram initData. Mini App musi byc otwarty przez bota Telegram (a nie bezposrednio w przegladarce).",
      );
    }
    log("[main] authenticate przez POST /api/auth/telegram");
    const auth = await authenticate(telegram.initData);
    log("[main] auth OK, player " + auth.player.displayName + " Lv " + auth.player.level);
    playerFull = await loadMe();
    log("[main] loadMe OK po authenticate");
  }

  log("[main] full state OK, w lokacji '" + playerFull.location.namePl + "'");

  // Boot Phaser z prawdziwymi danymi gracza w registry.
  // DPR-aware render config dla mobile crispness — Phaser default renderuje canvas
  // w CSS pikselach, browser skaluje × devicePixelRatio → rozmazanie na high-DPI mobile.
  // antialias + Text.setResolution(DPR) w scenach rozwiązuje emoji/HUD blur.
  const phaserConfig: Phaser.Types.Core.GameConfig = {
    type: Phaser.AUTO,
    parent: "game",
    backgroundColor: telegram.themeParams.bg_color ?? "#000000",
    scale: {
      mode: Phaser.Scale.RESIZE,
      autoCenter: Phaser.Scale.CENTER_BOTH,
      width: window.innerWidth,
      height: window.innerHeight,
    },
    render: {
      antialias: true,
      pixelArt: false,
      roundPixels: false,
    },
    callbacks: {
      preBoot: (game) => {
        game.registry.set("telegram", telegram);
        game.registry.set("config", config);
        game.registry.set("playerFull", playerFull);
        const dpr = Math.min(window.devicePixelRatio || 1, 3);
        game.registry.set("dpr", dpr);
        log("[main] phaser preBoot dpr=" + dpr + " (devicePixelRatio=" + window.devicePixelRatio + ")");
      },
      postBoot: () => log("[main] phaser postBoot"),
    },
    scene: [BootScene, WorldScene],
  };

  new Phaser.Game(phaserConfig);
  log("[main] Phaser.Game constructed — wszystko zywe");
}

bootstrap().catch((err: unknown) => {
  const msg = err instanceof Error ? err.message : String(err);
  log("[main] CRITICAL bootstrap error: " + msg);

  // Pokaz blad na UI niezaleznie od debug mode (zawsze widoczny dla kraytcznych bledow)
  const errDiv = document.createElement("div");
  errDiv.style.cssText = [
    "position:fixed",
    "top:50%",
    "left:10px",
    "right:10px",
    "transform:translateY(-50%)",
    "background:#330000",
    "color:#fff",
    "padding:20px",
    "border:2px solid #ff4444",
    "border-radius:6px",
    "font-family:sans-serif",
    "font-size:14px",
    "line-height:1.5",
    "z-index:99999",
    "max-height:80vh",
    "overflow-y:auto",
  ].join(";");
  errDiv.innerHTML =
    "<strong>⚠️ Blad ladowania gry</strong><br><br>" +
    msg +
    "<br><br><small>Odswiez aplikacje i sprobuj ponownie. Jesli blad sie powtarza, skontaktuj sie z administratorem.</small>";
  document.body.appendChild(errDiv);
});
